import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MultiImageUploader } from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDailyLife() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    content: { fr: '', en: '' },
    images: [],
    video_url: '',
    is_published: true,
    publish_date: ''
  });
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/daily-life?published_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/daily-life/${editingItem.id}`, formData);
        toast.success('Publication mise à jour');
      } else {
        await axios.post(`${API}/admin/daily-life`, formData);
        toast.success('Publication créée');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/admin/daily-life/${id}`);
      toast.success('Supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || { fr: '', en: '' },
      content: item.content || { fr: '', en: '' },
      images: item.images || [],
      video_url: item.video_url || '',
      is_published: item.is_published !== false,
      publish_date: item.publish_date || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: { fr: '', en: '' }, content: { fr: '', en: '' }, images: [], video_url: '', is_published: true, publish_date: '' });
    setImageUrl('');
  };

  const addImage = () => {
    if (imageUrl) {
      setFormData({ ...formData, images: [...formData.images, imageUrl] });
      setImageUrl('');
    }
  };

  return (
    <div data-testid="admin-daily-life">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_daily_life')}</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
          <Plus className="h-4 w-4 mr-2" />{t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm overflow-hidden">
              {item.images?.[0] && (
                <div className="h-40">
                  <img src={resolveImageUrl(item.images[0])} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#0F172A] mb-1">{getLocalizedContent(item.title)}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{getLocalizedContent(item.content)}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.is_published ? 'Publié' : 'Brouillon'}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? 'Modifier' : 'Nouvelle publication'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Titre (FR) *</Label><Input value={formData.title.fr} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })} required /></div>
              <div><Label>Titre (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Contenu (FR) *</Label><Textarea value={formData.content.fr} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, fr: e.target.value } })} rows={4} required /></div>
              <div><Label>Contenu (EN)</Label><Textarea value={formData.content.en} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, en: e.target.value } })} rows={4} /></div>
            </div>
            <div>
              <MultiImageUploader
                label="Images"
                values={formData.images}
                onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                testIdPrefix="daily-life-images"
              />
            </div>
            <div><Label>URL vidéo (YouTube, etc.)</Label><Input value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} /></div>
            <div><Label>Date de publication</Label><Input type="date" value={formData.publish_date} onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={formData.is_published} onCheckedChange={(v) => setFormData({ ...formData, is_published: v })} /><Label>Publié</Label></div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('common_cancel')}</Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">{t('common_save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
